import {css, useTheme} from '@mui/material';
import {GetStylesFunction, IconHeaderWithSearch, MaximizeButton, mixins, NoDataMessageWithIcon, StringUtils} from '@wallaby-ui/core';
import {
  ChartColor,
  getBarChartItems,
  LightChart,
  getBarChartHeight,
  LightChartLayout,
  useFormatters,
  LightChartLegend2,
} from '@wallaby-ui/document';
import {sortBy} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';

import {ChartDataType, ChartType, CHART_COLORS, CHART_ELLIPSIS_MAX_LENGTH} from './types';
import {useMaximizeWrapper} from './useMaximizeWrapper';

type Props = {
  title: string;
  data: ChartDataType[];
  isLoading: boolean;
  dataKeys: [string, string, string]; // Always 3 keys for low, medium, high
  chartType: ChartType;
  fractionDigits?: number;
  dataName?: string;
};

const getChartTitleKey = (chartType: ChartType): string => {
  switch (chartType) {
    case 'absolute':
      return 'hospital.network.report.charts.absolute.name';
    case 'caredays':
      return 'hospital.network.report.charts.caredays.name';
    default:
      return 'hospital.network.report.charts.absolute.name';
  }
};

const useChartData = (
  data: ChartDataType[],
  dataKeys: [string, string, string],
  chartColors: ChartColor[],
  fractionDigits: number | undefined
) => {
  return useMemo(() => {
    const sortedData = sortBy(data, (v) => v.organizationLabel);

    return getBarChartItems(sortedData, chartColors, (v, i) => {
      let x = [
        v[dataKeys[0] as keyof ChartDataType] as number,
        v[dataKeys[1] as keyof ChartDataType] as number,
        v[dataKeys[2] as keyof ChartDataType] as number,
      ][i];
      if (fractionDigits != null && fractionDigits >= 0) {
        if (fractionDigits === 0) {
          x = Math.round(x);
        } else {
          x = parseFloat(x.toFixed(fractionDigits));
        }
      }
      const y = v.organizationLabel;
      return {
        x,
        y: StringUtils.doEllipsis(y, CHART_ELLIPSIS_MAX_LENGTH),
        yTooltip: y,
      };
    });
  }, [data, chartColors, dataKeys, fractionDigits]);
};

const useChartLayout = (dataLength: number, xAxisFrom0: boolean = false): LightChartLayout => {
  return useMemo(() => {
    const chartHeight = getBarChartHeight(dataLength);
    return {
      margin: {},
      xaxis: {
        nticks: 10,
        automargin: true,
        // NOTE: only emit rangemode when forcing zero; an explicit `undefined` would clobber
        // LightChart's `nonnegative` default and let the empty x-axis fall back to Plotly's
        // ~[-1, 6] range when every legend series is deselected.
        ...(xAxisFrom0 ? {rangemode: 'tozero' as const} : {}),
      },
      yaxis: {
        automargin: true,
        fixedrange: true,
      },
      showlegend: false,
      barmode: 'stack',
      height: chartHeight,
    };
  }, [dataLength, xAxisFrom0]);
};

export const HospitalReportChart: React.FC<Props> = ({title, data, isLoading, dataKeys, chartType, fractionDigits, dataName}) => {
  const {t} = useFormatters();

  const [_, setPlanRerender] = useState(0);
  const chartColorHiddenMap = useRef({} as Record<string | number, boolean>);
  const onSetHiddenMap = useCallback(() => {
    setPlanRerender((v) => v + 1);
  }, []);
  const chartColors: ChartColor[] = CHART_COLORS.map((color) => ({
    id: t(color.label),
    label: t(`hospital.network.report.charts.absolute.legend.${color.label.split('.').pop()}`),
    color: color.color,
    hidden: chartColorHiddenMap.current ? chartColorHiddenMap.current[t(color.label)] ?? false : false,
  }));

  const chartData = useChartData(data, dataKeys, chartColors, fractionDigits);
  const chartDataXSum = chartData.flatMap((obj) => obj.x).reduce((acc, num) => acc + num, 0);
  const xAxisFrom0 = chartDataXSum === 0;
  const chartLayout = useChartLayout(data.length, xAxisFrom0);

  const downloadTitle = useMemo(() => getChartTitleKey(chartType), [chartType]);

  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} />, []);
  const theme = useTheme();
  const muiStyles = useMemo(() => getStyles({theme, params: {isLoading}}), [theme, isLoading]);

  const {
    maximized: chartMaximized,
    toggleMaximized: chartToggleMaximized,
    MaximizeWrapper: ChartMaximizeWrapper,
  } = useMaximizeWrapper(dataName);

  return (
    <ChartMaximizeWrapper>
      <IconHeaderWithSearch iconName='analytics1' label={title} useFixedViewBox='0 -1 16 14' iconCss={muiStyles.graphIcon}>
        <MaximizeButton maximized={chartMaximized} toggleMaximized={chartToggleMaximized} />
      </IconHeaderWithSearch>
      <div css={muiStyles.chartLegendChartBox}>
        <LightChartLegend2
          items={chartColors}
          hiddenMap={chartColorHiddenMap}
          onSetHiddenMap={onSetHiddenMap}
          legendRowStyles={{marginLeft: 'auto'}}
          textColor={'black'}
        />
        <LightChart
          imageDownload={{
            title: {
              text: t(downloadTitle),
            },
            showlegend: true,
          }}
          data={chartData}
          layout={chartLayout}
          isLoading={isLoading}
          chartStyles={{
            overflow: 'initial',
          }}
          noDataComponent={noDataComponent}
        />
      </div>
    </ChartMaximizeWrapper>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  graphIcon: css`
    width: 25px;
    height: 30px;
  `,
  chartLegendChartBox: css`
    ${mixins.contentBox};
    border-radius: 8px;
    background: #ffffff;
    margin-top: 5px;
    flex-grow: ${options?.params?.isLoading ? 1 : 0};
  `,
});
